import React, { useEffect, useState } from 'react';
import type { AvatarProps, AvatarShape, AvatarSize, AvatarStatus } from './types';

// ─── Size class map (exported for snapshot testing) ───────────────────────────

export const SIZE_CLASSES: Record<
  AvatarSize,
  { container: string; text: string; icon: string; dot: string; dotRing: string }
> = {
  sm: { container: 'h-8 w-8',   text: 'text-xs font-semibold',  icon: 'h-4 w-4',   dot: 'h-2 w-2',     dotRing: 'ring-[1.5px]' },
  md: { container: 'h-10 w-10', text: 'text-sm font-semibold',  icon: 'h-5 w-5',   dot: 'h-2.5 w-2.5', dotRing: 'ring-2' },
  lg: { container: 'h-12 w-12', text: 'text-sm font-semibold',  icon: 'h-6 w-6',   dot: 'h-3 w-3',     dotRing: 'ring-2' },
};

// ─── Initials color palette (exported for testing) ────────────────────────────
// 8 hues from the preset palette. Amber uses dark text for WCAG AA contrast.

export const INITIALS_COLORS: string[] = [
  'bg-brand-500 text-white',
  'bg-violet-500 text-white',
  'bg-pink-500 text-white',
  'bg-teal-500 text-white',
  'bg-success-600 text-white',
  'bg-warning-500 text-neutral-900',
  'bg-danger-500 text-white',
  'bg-indigo-500 text-white',
];

// ─── Internal constants ───────────────────────────────────────────────────────

const SHAPE_CLASSES: Record<AvatarShape, string> = {
  circle:  'rounded-full',
  rounded: 'rounded-lg',
};

const STATUS_DOT_CLASSES: Record<AvatarStatus, string> = {
  online:  'bg-success-500',
  away:    'bg-warning-500',
  offline: 'bg-neutral-400',
};

const STATUS_LABELS: Record<AvatarStatus, string> = {
  online:  'Online',
  away:    'Away',
  offline: 'Offline',
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

// djb2 hash → stable, deterministic color index for a given name
function hashName(name: string): number {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h) ^ name.charCodeAt(i);
    h = h >>> 0; // keep unsigned 32-bit
  }
  return h % INITIALS_COLORS.length;
}

// First + last word initials; single-word names use first 2 chars
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── StatusDot (internal) ─────────────────────────────────────────────────────
// Lives on the outer relative wrapper, NOT inside overflow-hidden, so it's never clipped.

function StatusDot({ status, s }: { status: AvatarStatus; s: (typeof SIZE_CLASSES)[AvatarSize] }) {
  return (
    <span
      role="img"
      aria-label={STATUS_LABELS[status]}
      className={[
        'absolute bottom-0 right-0 translate-x-[10%] translate-y-[10%]',
        'rounded-full ring-white',
        s.dot,
        s.dotRing,
        STATUS_DOT_CLASSES[status],
      ].join(' ')}
    />
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

export function Avatar({
  src,
  name,
  size = 'md',
  status,
  shape = 'circle',
  loading = false,
  className,
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  // Reset error state whenever the src URL changes
  useEffect(() => {
    setImgError(false);
  }, [src]);

  const s = SIZE_CLASSES[size];
  const shapeClass = SHAPE_CLASSES[shape];
  const ariaLabel = name ?? 'Avatar';

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <span className="relative inline-flex shrink-0">
        <span
          role="img"
          aria-label={ariaLabel}
          aria-busy="true"
          className={[
            'bg-neutral-200 animate-pulse-soft inline-block',
            s.container,
            shapeClass,
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {status && <StatusDot status={status} s={s} />}
      </span>
    );
  }

  // ── Variant resolution ─────────────────────────────────────────────────────
  const showImage    = !!src && !imgError;
  const showInitials = !showImage && !!name;
  // showIcon is the implicit fallback when neither showImage nor showInitials

  return (
    <span className="relative inline-flex shrink-0">
      <span
        role="img"
        aria-label={ariaLabel}
        className={[
          'inline-flex items-center justify-center overflow-hidden select-none',
          s.container,
          shapeClass,
          showInitials ? INITIALS_COLORS[hashName(name!)] : '',
          !showImage && !showInitials ? 'bg-neutral-200 text-neutral-500' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showImage && (
          <img
            src={src}
            alt={name ?? ''}
            aria-hidden="true"
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        )}

        {showInitials && (
          <span aria-hidden="true" className={s.text}>
            {getInitials(name!)}
          </span>
        )}

        {!showImage && !showInitials && (
          <svg
            aria-hidden="true"
            className={s.icon}
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Generic user silhouette — head + shoulders */}
            <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4Z" />
          </svg>
        )}
      </span>

      {status && <StatusDot status={status} s={s} />}
    </span>
  );
}
